import React from "react";

export default function ReservationSummaryView({ reservation, onClose }) {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const calculateDays = () => {
    if (!reservation.startDate || !reservation.endDate) return 0;
    const start = reservation.startDate.toDate ? reservation.startDate.toDate() : new Date(reservation.startDate);
    const end = reservation.endDate.toDate ? reservation.endDate.toDate() : new Date(reservation.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || 1;
  };

  const days = calculateDays();
  const dailyRate = parseFloat(reservation.dailyRate) || 0;
  const subtotal = dailyRate * days;
  const tax = 0; // This screen is a reservation estimate; Billing owns tax invoices.
  const total = parseFloat(reservation.totalCost) || (subtotal + tax);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Print-friendly content */}
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 border-b-2 border-burgundy pb-4">
            <div>
              <h1 className="text-3xl font-bold text-burgundy">VentES</h1>
              <p className="text-gray-600 mt-1">Equipment Rental Management</p>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-navy">RESERVATION ESTIMATE</h2>
              <p className="text-gray-600 mt-1">#{reservation.id?.substring(0, 8).toUpperCase()}</p>
              <p className="text-sm text-gray-500">Date: {formatDate(reservation.createdAt)}</p>
            </div>
          </div>

          <p className="mb-4">Estimated hire charges before tax. This is not a tax invoice or payment receipt. <a href="https://ventes-billing.netlify.app">Open VentES Billing</a></p>
          {/* Customer & Reservation Info */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-gray-700 mb-2">Reserved by:</h3>
              <p className="text-gray-600">{reservation.customerName || 'Customer'}</p>
              <p className="text-gray-600">{reservation.customerEmail || ''}</p>
            </div>
            <div>
              <h3 className="font-bold text-gray-700 mb-2">Reservation Details:</h3>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Start Date:</span> {formatDate(reservation.startDate)}</p>
                <p><span className="font-medium">End Date:</span> {formatDate(reservation.endDate)}</p>
                <p><span className="font-medium">Status:</span> <span className="capitalize">{reservation.status || 'pending'}</span></p>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <table className="w-full mb-8">
            <thead>
              <tr className="bg-gray-100 border-b-2 border-gray-300">
                <th className="text-left py-3 px-4">Item Description</th>
                <th className="text-center py-3 px-4">Days</th>
                <th className="text-right py-3 px-4">Daily Rate</th>
                <th className="text-right py-3 px-4">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-4 px-4">
                  <p className="font-medium">{reservation.itemName || 'Unknown Item'}</p>
                  <p className="text-sm text-gray-600">{reservation.category}</p>
                  {reservation.size && (
                    <p className="text-sm text-gray-600">Size: {reservation.size}</p>
                  )}
                </td>
                <td className="text-center py-4 px-4">{days}</td>
                <td className="text-right py-4 px-4">${dailyRate.toFixed(2)}</td>
                <td className="text-right py-4 px-4">${subtotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Tax (not calculated):</span>
                <span className="font-medium">Calculated in Billing</span>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-gray-300">
                <span className="text-lg font-bold">Total:</span>
                <span className="text-lg font-bold text-green-600">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {reservation.customerNotes && (
            <div className="mb-8">
              <h3 className="font-bold text-gray-700 mb-2">Notes:</h3>
              <p className="text-gray-600 text-sm">{reservation.customerNotes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-300 pt-4 text-center text-sm text-gray-600">
            <p>Thank you for your business!</p>
            <p className="mt-1">For questions, please contact VentES support.</p>
          </div>
        </div>

        {/* Action Buttons (hidden when printing) */}
        <div className="border-t border-gray-200 p-4 flex justify-end space-x-3 print:hidden">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-burgundy text-white rounded hover:bg-opacity-90 transition"
          >
            Print Reservation summary
          </button>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .fixed.inset-0 * {
            visibility: visible;
          }
          .fixed.inset-0 {
            position: static;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
